import WebClient from '../utils/web-client';
import OrderTypes from '../action-types/supportorders';
import { getFormattedDate } from '../utils/date-utils';
import { getUserFormData, getUserId } from '../selectors/user';
import { getCSOCFormData, getFilledCSOCFormPdfs } from '../selectors/childsupport';

const CSOC_MODIFY_FORM_ID = 4;
const FEE_WAIVER_FORM_ID = 5;

const setSupportOrders = payload => ({
    type: OrderTypes.SET_USER_SUPPORT_ORDERS,
    payload,
});

const getSupportOrders = () => async (dispatch) => {
    try {
        const { data } = await WebClient.get('/users/csoc');
        return dispatch(setSupportOrders({ orders: data.orders || [] }));
    } catch (err) {
        return [];
    }
};

export const setCSOCESignature = eSignature => ({
    type: OrderTypes.SET_CSOC_ESIGNATURE,
    payload: { eSignature },
});

export const setCSOCBasicInfo = formData => ({
    type: OrderTypes.SET_CSOC_BASIC_INFO,
    payload: { formData },
});

const postCSOCModifyRecordFormSuccess = filledFormPdfs => ({
    type: OrderTypes.POST_CSOC_FORM_SUCCESS,
    payload: { filledFormPdfs },
});

const postCSOCFormBegin = () => ({
    type: OrderTypes.POST_CSOC_FORM_BEGIN,
});

const postCSOCFormError = errorMsg => ({
    type: OrderTypes.POST_CSOC_FORM_ERROR,
    payload: { errorMsg },
});

const postCSOCFormDownloadBegin = () => ({
    type: OrderTypes.POST_CSOC_FORM_DOWNLOAD_BEGIN,
});

const postCSOCFormDownloadSuccess = (PDFDownloadLink, responseCourtInfo, responseSupportOfficeInfo) => ({
    type: OrderTypes.POST_CSOC_FORM_DOWNLOAD_SUCCESS,
    payload: { PDFDownloadLink, responseCourtInfo, responseSupportOfficeInfo },
});

const postCSOCFormDownloadError = errorMsg => ({
    type: OrderTypes.POST_CSOC_FORM_DOWNLOAD_ERROR,
    payload: { errorMsg },
});

const postCSOCFormDownload = () => (
    async (dispatch, getState) => {
        const filledForms = getFilledCSOCFormPdfs(getState());

        const payload = {
            ids: filledForms.map(f => f.id),
        };

        dispatch(postCSOCFormDownloadBegin());

        try {
            const response = await WebClient.post('/forms/download', payload);

            dispatch(postCSOCFormDownloadSuccess(response.data.url, response.data.courts, response.data.supportOffices));
        } catch (error) {
            dispatch(postCSOCFormDownloadError(error));
        }
    }
);

const postCSOCModifyForm = async (userFormData, ccapData, kcNumber, userId, caseData, dispatch) => {
    const petitionerName = `${userFormData.firstName} ${userFormData.lastName}`.toUpperCase();
    const kidsCase = caseData.payeeKIDSCases.find(({ payeeKIDSCaseNumber }) => payeeKIDSCaseNumber === kcNumber);

    const respondentName = `${kidsCase.cpFirstName} ${kidsCase.cpLastName}`;
    const arrearsOrders = kidsCase.courtOrders.filter(({ debtType }) => debtType.includes('Arrears'));
    const arrearsAmount = arrearsOrders.reduce((acc, val) => acc + (val.debtAmount ? parseFloat(val.debtAmount) : 0), 0);
    const arrearsOrder = kidsCase.courtOrders.find(({ debtType }) => debtType.includes('Arrears'));
    const supportOrder = kidsCase.courtOrders.find(({ debtType }) => debtType.includes('Current Support'));
    const isSupport = !!supportOrder;
    const isArrears = !!arrearsOrder;

    let circumstancesEmploymentParty = null;
    let circumstancesIncomeParty = null;
    let county = null;

    if (userFormData.county) {
        const { county: userFormDataCounty } = userFormData;
        county = userFormDataCounty;
    } else if (ccapData.county) {
        const { county: ccapDataCounty } = ccapData;
        county = ccapDataCounty;
    }

    if (userFormData.employment && !userFormData.employmentOther) {
        circumstancesEmploymentParty = petitionerName;
    } else if (userFormData.employmentOther && !userFormData.employment) {
        circumstancesEmploymentParty = respondentName;
    }

    if (userFormData.income && !userFormData.incomeOther) {
        circumstancesIncomeParty = petitionerName;
    } else if (userFormData.incomeOther && !userFormData.income) {
        circumstancesIncomeParty = respondentName;
    }

    let courtOrderDate = null;

    if (isSupport && supportOrder) {
        courtOrderDate = supportOrder.effectiveDate;
    } else if (isArrears && arrearsOrder) {
        courtOrderDate = arrearsOrder.effectiveDate;
    }

    const basePayload = {
        0: {
            county,
            caseNumber: caseData.courtCaseNumber,
            legalCustody: userFormData.legalCustody || null,
            physicalPlacement: userFormData.physicalPlacement || null,
            childSupport: isSupport,
            maintenance: userFormData.maintenance || null,
            arrears: isArrears,
            otherMotion: userFormData.otherMotion || null,
            otherMotionText: userFormData.otherMotionText || null,
            petitionerName,
            petitionerAddress: userFormData.petitionerAddress ? `${userFormData.petitionerAddress}`.toUpperCase() : null,
            petitionerCity: userFormData.petitionerCity ? `${userFormData.petitionerCity}`.toUpperCase() : null,
            petitionerState: userFormData.petitionerState ? `${userFormData.petitionerState}`.toUpperCase() : null,
            petitionerZip: userFormData.petitionerZip,
            petitionerPhone: userFormData.phone,
            respondentName,
            respondentAddress: userFormData.respondentAddress ? `${userFormData.respondentAddress}`.toUpperCase() : null,
            respondentCity: userFormData.respondentCity ? `${userFormData.respondentCity}`.toUpperCase() : null,
            respondentState: userFormData.respondentState ? `${userFormData.respondentState}`.toUpperCase() : null,
            respondentZip: userFormData.respondentZip,
            respondentPhone: userFormData.respondentPhone,
            csaIsParty: true,
            csaIsNotParty: null,
            to: respondentName,
        },
        1: {
            courtOrderDate,
            circumstancesChildLivesWithMe: userFormData.circumstancesChildLivesWithMe,
            circumstancesChildNotEligible: userFormData.circumstancesChildNotEligible,
            circumstancesMoving: userFormData.circumstancesMoving,
            circumstancesMovingParty: userFormData.circumstancesMovingParty || null,
            circumstancesBothMoving: userFormData.circumstancesBothMoving || null,
            circumstancesNotLivingTogether: userFormData.circumstancesNotLivingTogether,
            circumstancesPlacementSchedule: userFormData.circumstancesPlacementSchedule,
            circumstancesNotExercising: userFormData.circumstancesNotExercising || null,
            circumstancesEmploymentChanged: userFormData.employment || userFormData.employmentOther ? 'X' : null,
            circumstancesEmploymentParty,
            circumstancesBothPartyEmployment: userFormData.employment && userFormData.employmentOther,
            circumstancesIncomeChanged: userFormData.income || userFormData.incomeOther ? 'X' : null,
            circumstancesIncomeParty,
            circumstancesIncomeBothParty: userFormData.income && userFormData.incomeOther,
            circumstancesHealthInsurance: userFormData.circumstancesHealthInsurance,
            circumstancesRemarried: userFormData.circumstancesRemarried || null,
            circumstancesSafetyConcerns: userFormData.circumstancesSafetyConcerns || null,
            circumstancesOther: userFormData.circumstancesOther,
            circumstancesOtherExplained: userFormData.circumstancesOtherExplained,
            substantialChangeFacts: userFormData.substantialChangeFacts,
        },
        2: {
            legalCustody2: userFormData.legalCustody2 || null,
            custodyChildren: userFormData.custodyChildren || null,
            joint: userFormData.joint || null,
            sole: userFormData.sole || null,
            soleParty: userFormData.soleParty || null,
            custodyOther: userFormData.custodyOther || null,
            custodyOtherText: userFormData.custodyOtherText || null,
            physicalPlacement2: userFormData.physicalPlacement2 || null,
            placementChildren: userFormData.placementChildren || null,
            myPlacementSchedule: userFormData.myPlacementSchedule || null,
            otherParentPlacementSchedule: userFormData.otherParentPlacementSchedule || null,
            supervisedYes: userFormData.supervisedYes || null,
            supervisedParty: userFormData.supervisedParty || null,
            supervisedNo: userFormData.supervisedNo || null,
            childSupport2: isSupport,
            deviate: userFormData.deviate || null,
            deviateReasons: userFormData.deviateReasons || null,
            newAmount: userFormData.newAmount || null,
            noAdjustment: userFormData.noAdjustment || null,
            includeAdjustment: userFormData.includeAdjustment || null,
            adjustmentAmount: userFormData.adjustmentAmount || null,
            adjustmentUpward: userFormData.adjustmentUpward || null,
            adjustmentDownward: userFormData.adjustmentDownward || null,
        },
        3: {
            maintenance2: userFormData.maintenance2 || null,
            maintenanceParty: userFormData.maintenanceParty || null,
            maintenanceCourtAmount: userFormData.maintenanceCourtAmount || null,
            maintenanceNewAmount: userFormData.maintenanceNewAmount || null,
            maintenanceNewDollars: userFormData.maintenanceNewDollars || null,
            maintenanceNewPeriod: userFormData.maintenanceNewPeriod || null,
            arrears2: isArrears,
            arrearsParty: userFormData.arrearsParty || null,
            arrearsCourtAmount: isArrears ? true : null,
            arrearsNewAmount: userFormData.arrearsNewAmount || null,
            arrearsNewDollars: userFormData.arrearsNewDollars || null,
            arrearsNewPeriod: userFormData.arrearsNewPeriod || null,
            other2: userFormData.other2 || null,
            otherDescription: userFormData.otherDescription || null,
            additionalInfo: userFormData.additionalInfo || null,
            signature: userFormData.eSignature,
            printedName: petitionerName,
            signatureAddress: userFormData.petitionerAddress ? `${userFormData.petitionerAddress}`.toUpperCase() : null,
            signatureCityStateZip: userFormData.petitionerCity
                ? `${userFormData.petitionerCity}, ${userFormData.petitionerState} ${userFormData.petitionerZip}`.toUpperCase()
                : null,
            signatureEmail: userFormData.email,
            signaturePhone: userFormData.phone,
            signatureDate: getFormattedDate(new Date(), 'MM/dd/yyyy'),
        },
        userId,
    };

    const payload = {
        formData: {
            ...basePayload,
            county,
            caseNumber: `${caseData.courtCaseNumber}-${kcNumber}`,
            dob: userFormData.dob,
            date: getFormattedDate(new Date(), 'MM/dd/yyyy'),
        },
    };

    const response = await WebClient.post(`/forms/csoc/${CSOC_MODIFY_FORM_ID}/fill`, payload);

    const filledForms = [{
        ...response.data,
        createdAt: new Date(),
        type: 'csoc',
    }];

    if (userFormData.benefits) {
        const waiverPayload = {
            formData: {
                0: {
                    county,
                    petitioner: petitionerName,
                    respondent: respondentName,
                    caseNumber: caseData.courtCaseNumber,
                    unableToPay: true,
                    ssi: userFormData.ssi,
                    medicaid: userFormData.medicaid,
                    foodStamps: userFormData.foodStamps,
                    financialSituationNotChanged: true,
                },
                1: {
                    respondent2: petitionerName,
                    dateOfBirth: userFormData.dob,
                    address: `${userFormData.petitionerAddress}, ${userFormData.petitionerCity}, ${userFormData.petitionerState} ${userFormData.petitionerZip}`.toUpperCase(),
                    email: userFormData.email,
                    phone: userFormData.phone,
                },
                userId,
                county,
                address1: userFormData.petitionerAddress,
                city: userFormData.petitionerCity,
                state: userFormData.petitionerState,
                zip: userFormData.petitionerZip,
                caseNumber: `${caseData.courtCaseNumber}-${kcNumber} Fee Waiver`,
            },
        };

        try {
            const waiverResponse = await WebClient.post(`/forms/waiver/${FEE_WAIVER_FORM_ID}/fill`, waiverPayload);

            filledForms.push({
                ...waiverResponse.data,
                createdAt: new Date(),
                type: 'csoc',
            });
        } catch (err) {
            /* eslint-disable no-console */
            console.log(err);
            /* eslint-enable no-console */
        }
    }

    dispatch(postCSOCModifyRecordFormSuccess(filledForms));
};

export const postCSOCForm = (caseData, ccapData, kcNumber, onSuccess, onError) => async (dispatch, getState) => {
    const userFormData = getCSOCFormData(getState());
    const userId = getUserId(getState());

    const fullFormData = getUserFormData(getState());
    userFormData.dob = fullFormData.dob;

    dispatch(postCSOCFormBegin());

    try {
        await postCSOCModifyForm(userFormData, ccapData, kcNumber, userId, caseData, dispatch);
        dispatch(postCSOCFormDownload());
        onSuccess();
    } catch (error) {
        dispatch(postCSOCFormError(error));
        onError();
    }
};

export default getSupportOrders;
